import './styles.scss';

import { faX } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal } from '@mui/material';
import { useCallback, useState } from 'react';

import BinanceLogoSVG from '../../../../assets/Binance-logo.svg';
import BitfinexDarkLogoSVG from '../../../../assets/bitfinex-dark-logo.svg';
import BitgetDarkLogoSVG from '../../../../assets/bitget-dark-logo.svg';
import BitmexDarkLogoSVG from '../../../../assets/bitmex-dark-logo.svg';
import BullishLightLogoSVG from '../../../../assets/bullish-light-logo.svg';
import BybitDarkLogoSVG from '../../../../assets/bybit-dark-logo.svg';
import CoinbaseDarkLogoSVG from '../../../../assets/coinbase-dark-logo.svg';
import CoincallLogoSVG from '../../../../assets/coincall-logo.svg';
import DeribitLogoSVG from '../../../../assets/deribit-light-logo.svg';
import EdxmDarkLogoSVG from '../../../../assets/edxm-dark-logo.svg';
import GateDarkLogoSVG from '../../../../assets/gate-dark-logo.svg';
import GeminiDarkLogoSVG from '../../../../assets/gemini-dark-logo.svg';
import KrakenDarkLogoSVG from '../../../../assets/kraken-dark-logo.svg';
import MexcDarkLogoSVG from '../../../../assets/mexc-dark-logo.svg';
import OKXLogoSVG from '../../../../assets/Okx-logo.svg';
import { getSessionTracker } from '../../../../hooks/use-session-tracker';
import ComingSoonModal from '../coming-soon-modal';
import NewAccountModal from '../new-account-modal';

interface IProps {
  open: boolean;
  handleClose: () => void;
}

interface AccountType {
  name: string;
  label: string;
  icon: string;
  disabled?: boolean;
  comingSoon?: boolean;
}

const ACCOUNT_TYPES: AccountType[] = [
  { name: 'deribit', label: 'Deribit', icon: DeribitLogoSVG },
  { name: 'bullish', label: 'Bullish', icon: BullishLightLogoSVG },
  { name: 'okx', label: 'Okx', icon: OKXLogoSVG },
  { name: 'binance', label: 'Binance', icon: BinanceLogoSVG },
  { name: 'coincall', label: 'Coincall', icon: CoincallLogoSVG },
  { name: 'bitfinex', label: 'Bitfinex', icon: BitfinexDarkLogoSVG, comingSoon: true },
  { name: 'bitget', label: 'Bitget', icon: BitgetDarkLogoSVG, comingSoon: true },
  { name: 'bitmex', label: 'BitMEX', icon: BitmexDarkLogoSVG, comingSoon: true },
  { name: 'bybit', label: 'Bybit', icon: BybitDarkLogoSVG },
  { name: 'coinbase', label: 'Coinbase', icon: CoinbaseDarkLogoSVG, comingSoon: true },
  { name: 'edxm', label: 'EDXM', icon: EdxmDarkLogoSVG, comingSoon: true },
  { name: 'gate', label: 'Gate', icon: GateDarkLogoSVG, comingSoon: true },
  { name: 'gemini', label: 'Gemini', icon: GeminiDarkLogoSVG, comingSoon: true },
  { name: 'kraken', label: 'Kraken', icon: KrakenDarkLogoSVG, comingSoon: true },
  { name: 'mexc', label: 'MEXC', icon: MexcDarkLogoSVG, comingSoon: true },
];

const ConnectAccountModal = ({ open, handleClose }: IProps) => {
  const [openCreateAccountModal, setCreateAccountModal] = useState(false);
  const [openComingSoonModal, setOpenComingSoonModal] = useState(false);
  const [accountType, setAccountType] = useState<AccountType | undefined>();

  const handleCreateAccount = useCallback((accountType: AccountType) => {
    setCreateAccountModal(true);
    setAccountType(accountType);
  }, []);

  const handleComingSoonClick = useCallback((accountType: AccountType) => {
    const { tracker } = getSessionTracker();
    if (tracker) {
      tracker.event('coming_soon_exchange_clicked', { exchange: accountType.name });
    }
    setOpenComingSoonModal(true);
  }, []);

  const handleCloseComingSoon = useCallback(() => {
    setOpenComingSoonModal(false);
  }, []);

  const handleCloseCreateAccount = useCallback(() => {
    setCreateAccountModal(false);
    setAccountType(undefined);
  }, []);

  const handleAccountCreated = useCallback(() => {
    setCreateAccountModal(false);
    setAccountType(undefined);
    handleClose();
  }, [handleClose]);

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <div className="connect-new-account-modal-container">
          <div className="connect-new-account-modal-content">
            <div className="header">
              <h1>Connect a new account</h1>
              <Button className="close-button" onClick={handleClose}>
                <FontAwesomeIcon icon={faX} />
              </Button>
            </div>

            <div className="account-types">
              {ACCOUNT_TYPES.map((accountType) => (
                <div
                  className={`account-type ${accountType.disabled ? 'account-type-disabled' : ''}`}
                  key={accountType.name}
                >
                  <img src={accountType.icon} alt={`${accountType.label} icon`} />

                  <Button
                    className="button"
                    onClick={() =>
                      accountType.comingSoon
                        ? handleComingSoonClick(accountType)
                        : handleCreateAccount(accountType)
                    }
                    disabled={accountType.disabled}
                  >
                    Connect your {accountType.label} account
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      <NewAccountModal
        open={openCreateAccountModal}
        handleClose={handleCloseCreateAccount}
        accountType={accountType}
        handleCreate={handleAccountCreated}
      />

      <ComingSoonModal open={openComingSoonModal} handleClose={handleCloseComingSoon} />
    </>
  );
};

export default ConnectAccountModal;
